import data from "../../shared/sharedData";

export default {
  name: "Attorney or Agent Information (if applicable)",
  prefix: "D",
  pageInfo: {
    text: "<b><u>Important Note</u></b>: The attorney/agent information in this Section, specifically the name, telephone number, and email address, must be different from the employer’s point of contact information in Section C, unless the person in Section D is an employee of the employer.",
    coloring: "grey",
  },
  sections: [
    {
      fields: [
        {
          widget: "inlinePrefiller",
          prefillLabel:
            "Select an Attorney/Agent profile to populate this section (optional)",
          code: "D",
          prefill: {
            schema: "agentAttorney",
            type: "agent-attorney-individs",
            formName: "9142A",
            formSection: "D",
          },
        },
        {
          isGroup: {
            code: "D.1",
            machine_name: "d1_representation_type",
            label:
              "Indicate the type of representation for the employer in the filing of this application.",
            tooltip:
              "Identify whether the employer is represented by an attorney or agent in the process of filing this application. Only mark one box. <br><strong><u>Note:</u></strong> If “Attorney” “or “Agent” is selected, complete the remainder of Section D. If “None,” skip Questions 2 to 21 and continue to Section E.",
            coloring: "none",
            fields: [
              {
                widget: "radio",
                choice: "Attorney",
                value: "Attorney",
                validate: ["required"],
              },
              {
                widget: "radio",
                choice: "Agent",
                value: "Agent",
                validate: ["required"],
              },
              {
                widget: "radio",
                choice: "None",
                value: "None",
                validate: ["required"],
              },
            ],
          },
        },
      ],
    },
    {
      isVisible: {
        when: "d1_representation_type",
        is: ["Attorney", "Agent"],
        operator: "||",
      },
      fields: [
        {
          code: "D.2",
          machine_name: "d2_agent_last",
          widget: "text",
          label: "Attorney or Agent's Last (family) Name",
          validate: [
            "required",
            {
              minLength: 2,
              message:
                "Attorney or Agent's Last Name must contain at least 2 characters.",
            },
          ],
          maxLength: 35,
          tooltip: "Enter the last (family) name of the attorney/agent.",
        },
        {
          code: "D.3",
          machine_name: "d3_agent_first",
          widget: "text",
          label: "First (given) Name",
          validate: [
            "required",
            {
              minLength: 2,
              message: "First Name must contain at least 2 characters.",
            },
            {
              uniqueNameCheck: "d3_agent_first",
              message:
                "Agent or Attorney's name cannot be the same as the Employer POC.",
              needsFieldValues: true,
              extraData: {
                firstName: "d3_agent_first",
                lastName: "d2_agent_last",
                sourceFirstName: "c2_poc_first",
                sourceLastName: "c1_poc_last",
              },
            },
          ],
          maxLength: 35,
          warning: {
            text: "In accordance with OMB approved instructions, the attorney/agent information contained in this section, specifically the name, telephone number, and email address, must be different from the employer's point of contact information in Section C of this application, unless the person named in this section is an employee of the employer.",
            isVisible: {
              when: "d2_agent_last d3_agent_first",
              is: ["c1_poc_last", "c2_poc_first"],
              operator: "compareMultiFieldValues",
            },
          },
          tooltip: "Enter the first (given) name of the attorney/agent.",
        },
        {
          code: "D.4",
          machine_name: "d4_agent_middle",
          widget: "text",
          label: "Middle name(s)",
          maxLength: 35,
          tooltip: "Enter the middle name of the attorney/agent.",
        },
      ],
    },
    {
      isVisible: {
        when: "d1_representation_type",
        is: ["Attorney", "Agent"],
        operator: "||",
      },
      fields: [
        {
          code: "D.5",
          machine_name: "d5_agent_address_1",
          widget: "text",
          label: "Address 1",
          validate: [
            "required",
            {
              minLength: 5,
              message: "Address 1 must contain at least 5 characters.",
            },
          ],
          maxLength: 60,
          tooltip: "Enter the street address of the attorney/agent.",
        },
        {
          code: "D.6",
          machine_name: "d6_agent_address_2",
          widget: "text",
          label:
            "<span>Address 2 <i>(apartment/suite/floor and number)</i></span>",
          maxLength: 60,
          tooltip:
            "If additional space is needed for the street address, use this line to complete the attorney/agent’s street address.",
        },
        {
          code: "D.10",
          machine_name: "d10_agent_country_id",
          widget: "select",
          label: "Country",
          selection: data.country,
          validate: ["required"],
          clearSchema: ["d8_agent_state_id"],
          tooltip:
            "Enter the country of the attorney/agent. If the city and country are the same, the name <u>must</u> still be entered in <u>both</u> fields.",
        },
        {
          code: "D.7",
          machine_name: "d7_agent_city",
          widget: "text",
          label: "City",
          validate: [
            "required",
            {
              minLength: 2,
              message: "City must contain at least 2 characters.",
            },
          ],
          maxLength: 35,
          tooltip:
            "Enter the city of the attorney/agent. If the city and country are the same, the name <u>must</u> still be entered in <u>both</u> fields.",
        },
        {
          code: "D.8",
          machine_name: "d8_agent_state_id",
          widget: "select",
          label: "State",
          state: true,
          selection: data.state,
          validate: ["required"],
          isVisible: {
            when: "d10_agent_country_id",
            is: "UNITED STATES OF AMERICA",
          },
          tooltip:
            "Enter the State, District, or Territory of the attorney/agent.",
        },
        {
          code: "D.9",
          machine_name: "d9_agent_zip",
          widget: "text",
          label: "Postal Code",
          validate: ["required", "postal"],
          maxLength: 10,
          tooltip: "Enter the postal (zip) code of the attorney/agent.",
        },
        {
          code: "D.11",
          machine_name: "d11_agent_province",
          widget: "text",
          label: "Province",
          maxLength: 25,
          validate: [
            {
              minLength: 5,
              message: "Province must contain at least 5 characters.",
            },
          ],
          tooltip: "Enter the province of the attorney/agent, if applicable.",
        },
      ],
    },
    {
      isVisible: {
        when: "d1_representation_type",
        is: ["Attorney", "Agent"],
        operator: "||",
      },
      fields: [
        {
          code: "D.12",
          machine_name: "d12_agent_phone",
          widget: "phone",
          label: "Telephone Number",
          validate: ["required", "phone"],
          warning: {
            text: "In accordance with OMB approved instructions, the attorney/agent information contained in this section, specifically the name, telephone number, and email address, must be different from the employer's point of contact information in Section C of this application, unless the person named in this section is an employee of the employer.",
            isVisible: {
              when: "c12_poc_phone",
              is: "d12_agent_phone",
              operator: "compareFieldValues",
            },
          },
          tooltip:
            "Enter the area code and telephone number of the attorney/agent. Include country code, if applicable.",
        },
        {
          code: "D.13",
          machine_name: "d12_agent_phone_ext",
          widget: "text",
          label: "Extension",
          validate: ["number"],
          parse: "positiveWholeNumber", //don't allow decimal Number and negative number
          maxLength: 4,
          tooltip:
            "Enter the extension of the telephone number of the attorney/agent, if applicable.",
        },
        {
          code: "D.14",
          machine_name: "d14_agent_email",
          widget: "text",
          label: "Law Firm/Business Email Address",
          validate: [
            "required",
            {
              email: {
                extraData: {
                  allowNAs: false,
                },
              },
            },
          ],
          maxLength: 55,
          warning: {
            text: "In accordance with OMB approved instructions, the attorney/agent information contained in this section, specifically the name, telephone number, and email address, must be different from the employer's point of contact information in Section C of this application, unless the person named in this section is an employee of the employer.",
            isVisible: {
              when: "c14_poc_email",
              is: "d14_agent_email",
              operator: "compareFieldValues",
            },
          },
          tooltip:
            "Enter the business email address of the attorney/agent in the format <a href='' target='_blank'>name@emailaddress.top-leveldomain</a>. The email entered in this field must be the one regularly used by the attorney/agent’s point of contact to send and receive electronic communications to and from the Department with respect to the processing of this application. If the attorney/agent’s point of contact does not possess a business email address, please enter “N/A.”",
        },
        {
          code: "D.15",
          machine_name: "d15_agent_firm_name",
          widget: "text",
          label: "Law Firm/Business Name",
          validate: [
            "required",
            { minLength: 5, message: "Enter the full legal business name" },
          ],
          maxLength: 120,
          tooltip: "Enter the attorney/agent’s law firm or business name.",
        },
        {
          code: "D.16",
          machine_name: "d16_agent_firm_fein",
          widget: "text",
          label: "Law Firm/Business FEIN",
          validate: ["required", "fein", { minLength: 10 }],
          parse: "fein",
          maxLength: 10,
          tooltip:
            "Enter the attorney/agent's law firm or business nine-digit FEIN assigned by the IRS.",
        },
      ],
    },
    {
      isVisible: {
        when: "d1_representation_type",
        is: "Attorney",
      },
      fields: [
        {
          code: "D.17",
          machine_name: "d17_agent_bar_number",
          widget: "text",
          label: "State Bar Number(s)",
          validate: ["required", { minLength: 2 }],
          maxLength: 35,
          tooltip:
            "Enter the attorney's State Bar number. If the attorney is licensed in more than one State, enter only one State Bar number. If submitting this form electronically and the attorney is licensed in a State that does not issue State Bar numbers, leave the field blank and once confirmed it will be automatically pre-populated with “N/A.” <br> <strong>Note:</strong> The answers to questions 18 and 19 below should correspond to the same state for which a Bar number was provided in question 17, if any.",
        },
        {
          code: "D.18",
          machine_name: "d18_agent_court_state_id",
          widget: "select",
          label: "State Bar Where Attorney is in Good Standing §",
          selection: data.state,
          state: true,
          validate: ["required"],
          tooltip:
            "Enter the <u>State</u> of the highest court where the attorney is in good standing.",
        },
        {
          code: "D.19",
          machine_name: "d19_agent_court_name",
          widget: "text",
          label:
            "Name of the Highest State Court where Attorney is in Good Standing §",
          maxLength: 55,
          tooltip:
            "Enter the <u>name</u> of the highest court in the State where the attorney is in good standing.",
        },
      ],
    },
    {
      isVisible: {
        when: "d1_representation_type",
        is: "Agent",
      },
      fields: [
        {
          code: "D.20",
          machine_name: "d20_is_agent_agreement_complete",
          label:
            "A copy of the current agreement or other documentation demonstrating the agent’s authority to represent the employer in this application is attached to this application.",
          tooltip:
            "Mark “Yes” to indicate that the employer is submitting a copy of the agent agreement or other documentation demonstrating the agent’s authority to represent the employer, as required. <i>See</i> 20 CFR 655.133(a).",
          coloring: "none",
          name: "Agent Agreement",
          widget: "file_upload",
          fileTypes: [".pdf", ".png", ".jpg"],
          validate: ["required", "checkValue"],
        },
        // V2
        {
          isGroup: {
            code: "D.21",
            machine_name: "d21_is_agent_mspa_complete",
            label:
              "A copy of the agent’s current Migrant and Seasonal Agricultural Worker Protection Act (MSPA) Certificate of Registration identifying the farm labor contracting activities the agent is authorized to perform is attached to this application.",
            tooltip:
              "Mark “Yes” to indicate that a copy of the current Migrant and Seasonal Agricultural Worker Protection Act (MSPA) Farm Labor Contractor (FLC) Certificate of Registration identifying the farm labor contracting activities the agent is authorized to perform is attached to this application, as required by 20 CFR 655.133(b). If the agent is not performing any activities related to this application that require the agent to obtain a MSPA FLC Certificate of Registration, mark “N/A”.",
            coloring: "none",
            isVisible: {
              when: "formVersionId",
              is: 2,
            },
            fields: [
              {
                widget: "radio",
                choice: "Yes",
                value: "YES",
                validate: ["required"],
              },
              // {
              //   widget: "radio",
              //   choice: "No",
              //   value: "NO",
              //   validate: ["required"],
              // },
              {
                widget: "radio",
                choice: "N/A",
                value: "N/A",
                validate: ["required"],
              },
            ],
          },
        },
        {
          isVisible: {
            when: "d21_is_agent_mspa_complete",
            is: "YES",
          },
          machine_name: "d21_is_agent_mspa_complete_file",
          name: "Agent MSPA Certificate",
          widget: "file_upload",
          fileTypes: [".pdf", ".png", ".jpg"],
          tooltip: "placeholder tooltip",
          reviewHidden: true,
          // validate: ['required','checkValue'],
        },
        {
          code: "I5",
          machine_name: "files",
          widget: "text",
          hidden: true,
        },
      ],
    },
  ],
};
